import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useRouter } from 'next/router'

import { Button, Stack, Typography } from '@mui/material'
import { LinkText } from '@core/components/common/Button'
import { ArrowLeftIcon } from '@core/components/icons'
import { CustomInput, RHFCustomSelect } from '@core/components/ui'
import { BackdropLoader } from '@core/components/ui/BackdropLoader/BackdropLoader'
import { AlertDialog } from '@core/components/ui/CustomDialog'
import { useLayout } from '@core/contexts/layout'
import { colors } from '@core/helpers'
import { SERVER_ERROR_MESSAGE } from '@core/lib/utils'
import useMiniAlertStore from '@core/stores/miniAlert/useMiniAlertStore'
import { zodResolver } from '@hookform/resolvers/zod'
import { useDocumentInputs } from '@hooks/useDocumentInputs'
import { verifyIfUserExists } from '@services/register'

import { LoginLayout } from '../LoginLayout'
import { initialForm, type ResetAccountFormData, sendEmailSchema } from './sendEmailSchema'

export const LoginReset = () => {
  const { isMobile } = useLayout()
  const router = useRouter()
  const { openAlert } = useMiniAlertStore()

  const [openNotRegisteredModal, setOpenNotRegisteredModal] = useState(false)

  const {
    control,
    handleSubmit,
    watch,
    clearErrors,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<ResetAccountFormData>({
    defaultValues: initialForm,
    resolver: zodResolver(sendEmailSchema)
  })

  const values = watch()

  const { onChangeDocumentNumber, documentTypes } = useDocumentInputs({
    clearErrors,
    setValue,
    watch,
    documentType: values.documentType
  })

  const onSubmit = async ({ documentNumber, documentType }: ResetAccountFormData) => {
    const centerId = 1
    // ? Antes de continuar se valida que el paciente exista con el documento ingresado
    const userExist = await verifyIfUserExists(centerId, documentType, documentNumber)

    if (userExist === null) {
      openAlert(SERVER_ERROR_MESSAGE)

      return
    }

    if (!userExist) {
      setOpenNotRegisteredModal(true)

      return
    }

    await router.push(
      `/autenticacion/iniciar-sesion/restablecer/cambiar-contrasena?dt=${documentType}&dn=${documentNumber}`
    )
  }

  return (
    <LoginLayout title='Restablecer cuenta - Portal aviva'>
      <LinkText
        typographyProps={{
          fontWeight: 600
        }}
        customIcon={(color, size) => <ArrowLeftIcon size={size} color={colors.primary_darkest} />}
        href='/autenticacion/iniciar-sesion/'
        text='Atrás'
        iconUbi='left'
        color={colors.primary_darkest}
        styleContainer={{
          marginTop: '2.4rem',
          marginBottom: isMobile ? '5.8rem' : undefined
        }}
      />
      <Stack
        justifyContent={isMobile ? undefined : 'center'}
        height={isMobile ? undefined : '70vh'}
        maxWidth={isMobile ? undefined : '39.2rem'}
      >
        <Stack mb={isMobile ? '2.4rem' : '1.6rem'}>
          <Typography
            component='h1'
            mb='1.6rem'
            color='secondary'
            variant={isMobile ? 'd3' : 'h1_bold'}
          >
            Olvidé mi contraseña
          </Typography>
          <Typography
            color='var(--color-neutral-darkest)'
            variant={isMobile ? 'body3_medium' : 'body2'}
            component='p'
          >
            Te enviaremos un enlace a tu correo asociado a tu nº de documento para que puedas
            cambiar tu contraseña.
          </Typography>
        </Stack>

        <Stack component='form' onSubmit={handleSubmit(onSubmit)}>
          <Stack direction={isMobile ? 'column' : 'row'} mb='4.2rem' width='100%' gap='2.4rem'>
            <RHFCustomSelect
              control={control}
              options={documentTypes ?? []}
              label='Tipo de documento'
              name='documentType'
              fullWidth
            />
            <CustomInput
              label='Nº documento'
              placeholder='Nº documento'
              value={values.documentNumber}
              onChange={onChangeDocumentNumber}
              name='documentNumber'
              error={Boolean(errors?.documentNumber)}
              helperText={errors?.documentNumber?.message}
              inputMode='numeric'
              fullWidth
            />
          </Stack>
          <Button size='large' type='submit'>
            Enviar enlace
          </Button>
        </Stack>
      </Stack>
      <AlertDialog
        open={openNotRegisteredModal}
        setOpen={setOpenNotRegisteredModal}
        type='info'
        description='Este usuario no se encuentra registrado actualmente'
        onClose={() => setOpenNotRegisteredModal(false)}
        title='Cuenta no registrada'
        footerButtonText='Registrarse'
        onContinue={() => router.push('/autenticacion/registro')}
      />
      <BackdropLoader loading={isSubmitting} title='Validando informacion...' />
    </LoginLayout>
  )
}
